import { useId, useMemo, useState } from 'react';

import { Campo, type OpcaoDeSelecao } from './Campo';
import { EtiquetaDaOpcao } from './EtiquetaDaOpcao';
import { filtrarOpcoes, textoInteiro } from './lista-de-escolha';

/**
 * Escolher VARIOS itens de uma lista, digitando para filtrar.
 *
 * Nasceu na autorizacao para operar (20/10/2026): a janela pedia o
 * identificador interno do modelo, que ninguem sabe de cabeca, e um modelo por
 * vez. Aqui a pessoa digita "320" e marca os modelos que aparecem; cada marcado
 * vira uma linha.
 */
export function CampoBuscaMultipla({
  rotulo,
  opcoes,
  marcados,
  aoMudar,
  opcional = false,
  ajuda,
  vazio = 'Nada cadastrado ainda.',
}: {
  readonly rotulo: string;
  readonly opcoes: readonly OpcaoDeSelecao[];
  readonly marcados: readonly string[];
  readonly aoMudar: (marcados: readonly string[]) => void;
  readonly opcional?: boolean;
  readonly ajuda?: string;
  /** O que dizer quando a lista inteira esta vazia (nao o filtro). */
  readonly vazio?: string;
}) {
  const id = useId();
  const [busca, setBusca] = useState('');
  /* A mesma busca do seletor: sem acento, pelo rótulo, pelas etiquetas e pela segunda linha. */
  const filtradas = useMemo(() => filtrarOpcoes(opcoes, busca), [busca, opcoes]);

  const alternar = (valor: string) => {
    aoMudar(marcados.includes(valor) ? marcados.filter((v) => v !== valor) : [...marcados, valor]);
  };

  return (
    <Campo
      id={id}
      rotulo={rotulo}
      opcional={opcional}
      {...(ajuda ? { ajuda } : {})}
    >
      <div className="flex flex-col gap-2">
        <input
          id={id}
          type="search"
          value={busca}
          onChange={(evento) => {
            setBusca(evento.target.value);
          }}
          placeholder="Digite para buscar"
          autoComplete="off"
          className="controle w-full"
        />
        <ul
          className="max-h-48 overflow-y-auto rounded-controle border border-borda bg-superficie p-1"
          aria-label={rotulo}
        >
          {opcoes.length === 0 ? (
            <li className="txt-apoio px-3 py-2 text-tinta-secundaria">{vazio}</li>
          ) : filtradas.length === 0 ? (
            <li className="txt-apoio px-3 py-2 text-tinta-secundaria">Nada com esse nome. Apague a busca ou tente outra palavra.</li>
          ) : (
            filtradas.map((opcao) => (
              <li key={opcao.valor}>
                <label
                  title={textoInteiro(opcao)}
                  className="flex cursor-pointer items-center gap-3 rounded-etiqueta px-3 py-2 hover:bg-rebaixada"
                >
                  <input
                    type="checkbox"
                    checked={marcados.includes(opcao.valor)}
                    onChange={() => {
                      alternar(opcao.valor);
                    }}
                  />
                  {/* As etiquetas e a segunda linha como no seletor (31/10/2026). */}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="txt-corpo text-tinta">{opcao.rotulo}</span>
                      {(opcao.etiquetas ?? []).map((e) => (
                        <EtiquetaDaOpcao key={`${e.tom}-${e.texto}`} etiqueta={e} />
                      ))}
                    </span>
                    {opcao.detalhe ? <span className="txt-linha2 truncate text-tinta-secundaria">{opcao.detalhe}</span> : null}
                  </span>
                </label>
              </li>
            ))
          )}
        </ul>
        {marcados.length > 0 ? (
          <span className="txt-apoio text-tinta-secundaria">
            {marcados.length === 1 ? '1 escolhido' : `${String(marcados.length)} escolhidos`}
          </span>
        ) : null}
      </div>
    </Campo>
  );
}
